import React, { useMemo, useState } from "react";
import { sectionTemplates } from "../../../../data/database";

const buildBg = (style, defaultClass) => {
  const mode = style?.bgMode || "default";

  if (mode === "solid" && style?.bgColor) {
    return { style: { backgroundColor: style.bgColor }, className: "" };
  }

  if (mode === "gradient") {
    const from = (style?.bgGradientFrom || "").trim();
    const via = (style?.bgGradientVia || "").trim();
    const to = (style?.bgGradientTo || "").trim();
    const angleNum = Number(style?.bgGradientAngle);
    const angle = Number.isFinite(angleNum) ? angleNum : 180;

    if (from && to) {
      const stops = via ? `${from}, ${via}, ${to}` : `${from}, ${to}`;
      return {
        style: { backgroundImage: `linear-gradient(${angle}deg, ${stops})` },
        className: "",
      };
    }
  }

  return { style: undefined, className: defaultClass };
};

const getAlignValue = (style) => {
  const v = String(style?.textAlign || style?.alignment || "").toLowerCase().trim();
  if (v === "left" || v === "center" || v === "right") return v;
  return "center";
};

const getAlignClass = (style) => {
  const a = getAlignValue(style);
  if (a === "left") return "text-left";
  if (a === "right") return "text-right";
  return "text-center";
};

const getJustifyClass = (style) => {
  const a = getAlignValue(style);
  if (a === "left") return "justify-start";
  if (a === "right") return "justify-end";
  return "justify-center";
};

const GallerySection2 = ({ config, style }) => {
  const [activeFilter, setActiveFilter] = useState("all");
  const [hoveredProject, setHoveredProject] = useState(null);

  const tpl = sectionTemplates?.gallery?.template2 || {};
  const cfg = { ...(tpl.config || {}), ...(config || {}) };
  const s = { ...(tpl.style || {}), ...(style || {}) };

  const projects = useMemo(() => (Array.isArray(cfg?.projects) ? cfg.projects : []), [cfg]);

  const filterButtons = useMemo(() => {
    if (Array.isArray(cfg?.filterButtons) && cfg.filterButtons.length) return cfg.filterButtons;

    const cats = Array.from(new Set(projects.map((p) => p.category).filter(Boolean)));
    return [{ id: "all", label: "All" }, ...cats.map((c) => ({ id: c, label: c }))];
  }, [cfg, projects]);

  const filteredProjects =
    activeFilter === "all"
      ? projects
      : projects.filter((p) => p.category === activeFilter);

  const bg = buildBg(s, "bg-white");
  const alignClass = getAlignClass(s);
  const justifyClass = getJustifyClass(s);

  return (
    <section
      dir={cfg?.direction || "ltr"}
      style={bg.style}
      className={`py-24 px-4 ${bg.className}`}
    >
      <div className="container mx-auto max-w-7xl">
        <div className={`${alignClass} mb-16`}>
          <h2
            style={{ color: s?.titleColor || undefined }}
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
          >
            {cfg?.title || "Portfolio"}
          </h2>

          <p
            style={{ color: s?.subtitleColor || undefined }}
            className="text-xl text-gray-600 max-w-3xl mx-auto mb-12"
          >
            {cfg?.subtitle || "Browse by category."}
          </p>

          <div className={`flex flex-wrap ${justifyClass} gap-4 mb-12`}>
            {filterButtons.map((filter) => {
              const active = activeFilter === filter.id;

              return (
                <button
                  key={filter.id}
                  onClick={() => setActiveFilter(filter.id)}
                  style={
                    active
                      ? {
                          backgroundColor:
                            s?.galleryFilterActiveBg || s?.primaryBtnBg || undefined,
                          color:
                            s?.galleryFilterActiveText || s?.primaryBtnText || undefined,
                        }
                      : {
                          backgroundColor: s?.galleryFilterInactiveBg || undefined,
                          color: s?.galleryFilterInactiveText || undefined,
                          borderColor: s?.galleryFilterInactiveBorder || undefined,
                        }
                  }
                  className={`px-6 py-3 rounded-full font-medium transition-all duration-300 border ${
                    active
                      ? "shadow-lg transform -translate-y-1"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200 border-transparent"
                  }`}
                >
                  {filter.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="columns-1 md:columns-2 lg:columns-3 gap-6 space-y-6">
          {filteredProjects.map((project) => (
            <div
              key={project.id}
              className="relative break-inside-avoid rounded-2xl overflow-hidden group cursor-pointer"
              onMouseEnter={() => setHoveredProject(project.id)}
              onMouseLeave={() => setHoveredProject(null)}
              onClick={() => {
                if (project?.url) window.open(project.url, "_blank", "noopener,noreferrer");
              }}
            >
              <div className="relative h-80 overflow-hidden">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />

                <div
                  style={{
                    background: s?.galleryOverlayColor
                      ? `linear-gradient(to top, ${s.galleryOverlayColor}, transparent, transparent)`
                      : undefined,
                  }}
                  className={`absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent transition-opacity duration-300 ${
                    hoveredProject === project.id ? "opacity-100" : "opacity-0"
                  }`}
                />
              </div>

              <div
                className={`${alignClass} absolute bottom-0 left-0 right-0 p-6 text-white transform transition-all duration-500 ${
                  hoveredProject === project.id
                    ? "translate-y-0 opacity-100"
                    : "translate-y-4 opacity-0"
                }`}
              >
                <h3
                  style={{ color: s?.galleryOverlayTextColor || undefined }}
                  className="text-xl font-bold"
                >
                  {project.title}
                </h3>
                {project?.description ? (
                  <p
                    style={{ color: s?.galleryOverlayTextColor || undefined }}
                    className="text-sm text-white/80 mt-1"
                  >
                    {project.description}
                  </p>
                ) : null}
              </div>
            </div>
          ))}
        </div>

        {filteredProjects.length === 0 && (
          <div
            style={{ color: s?.galleryEmptyStateColor || undefined }}
            className="text-center text-gray-500 mt-12"
          >
            {cfg?.emptyStateText || "No projects in this category yet."}
          </div>
        )}
      </div>
    </section>
  );
};

export default GallerySection2;